import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import SupportWidget from "../components/SupportWidget";
import PartnersMap from "../components/PartnersMap";
import {
  ArrowRight,
  Mail,
  Phone,
  Calendar,
  GraduationCap,
  Music,
  Store,
  Users,
  Utensils,
  Bus,
  Building2,
  Printer,
  ShoppingBag,
  Ticket,
  Wallet,
  ScanLine,
  UserPlus,
  Sparkles,
  KeyRound,
  Smartphone,
  ChevronDown,
} from "lucide-react";
import BrandMark from "../components/BrandMark";
import WhatsAppChannelCard from "../components/WhatsAppChannelCard";
import AdsRotator from "../components/AdsRotator";
import { useAuth } from "../contexts/AuthContext";

const CARD_IMG =
  "https://customer-assets.emergentagent.com/job_digital-identity-120/artifacts/ongisr65_C1980C3E-4E21-4BC8-9D47-F729E86B8BCD.PNG";

// "Made in Ghana" lifestyle hero — a Ghanaian woman in kente holding her
// physical iCaard. Used in the "Join the family" call-to-action so the
// landing page shows a real human face alongside the abstract card.
const LIFESTYLE_HERO_IMG =
  "https://customer-assets.emergentagent.com/job_digital-identity-120/artifacts/qzd6129j_EE2E3374-2467-4251-BDF4-3611BB6FEB4E.PNG";

/**
 * Free-form, hand-drawn-style background icon.  We keep `<svg>` strokes (no fills),
 * very low opacity and gentle random rotation/position so the page feels alive
 * but never noisy.
 */
function FloatIcon({ Icon, top, left, size = 36, rotate = 0, opacity = 0.07 }) {
  return (
    <Icon
      className="absolute text-white pointer-events-none select-none"
      strokeWidth={1.25}
      style={{
        top,
        left,
        width: size,
        height: size,
        opacity,
        transform: `rotate(${rotate}deg)`,
      }}
    />
  );
}

/* Hand-scattered icon field — mirrors the doodle aesthetic of the iCaard card. */
const DOODLES = [
  { Icon: Calendar,       top: "6%",  left: "4%",   size: 44, rotate: -12 },
  { Icon: GraduationCap,  top: "12%", left: "88%",  size: 50, rotate: 14 },
  { Icon: Music,          top: "22%", left: "20%",  size: 32, rotate: 22 },
  { Icon: Store,          top: "30%", left: "76%",  size: 46, rotate: -8 },
  { Icon: Users,          top: "44%", left: "8%",   size: 42, rotate: 6 },
  { Icon: Utensils,       top: "50%", left: "92%",  size: 38, rotate: -18 },
  { Icon: Bus,            top: "62%", left: "14%",  size: 50, rotate: 10 },
  { Icon: Building2,      top: "68%", left: "84%",  size: 44, rotate: -10 },
  { Icon: Printer,        top: "78%", left: "30%",  size: 36, rotate: 18 },
  { Icon: ShoppingBag,    top: "82%", left: "70%",  size: 40, rotate: -14 },
  { Icon: Ticket,         top: "88%", left: "10%",  size: 38, rotate: 8 },
  { Icon: Sparkles,       top: "92%", left: "90%",  size: 30, rotate: -6 },
  // upper-mid sprinkle
  { Icon: GraduationCap,  top: "18%", left: "52%",  size: 22, rotate: 0,   opacity: 0.05 },
  { Icon: Music,          top: "38%", left: "46%",  size: 20, rotate: 12,  opacity: 0.05 },
  { Icon: Store,          top: "55%", left: "44%",  size: 24, rotate: -16, opacity: 0.05 },
];

/** Steps explaining how iCaard works. */
const STEPS = [
  { n: 1, Icon: UserPlus, title: "Create your account", body: "Choose Customer, then register with your name, phone number, password and profile photo." },
  { n: 2, Icon: Wallet, title: "Explore your services", body: "Find food, transport, events, hostels, delivery, printing and professional services." },
  { n: 3, Icon: ScanLine, title: "Book and keep track", body: "Keep tickets, travel passes and gift cards in My Tickets. Track orders, queue positions and bookings." },
  { n: 4, Icon: Sparkles, title: "Manage your account", body: "Review your activity, manage your profile and get help from support agents." },
];

/**
 * Portal-selector — the public landing screen for first-time visitors who
 * haven't picked a portal yet.  After they pick one, PortalContext remembers
 * the choice forever (until logout-and-switch).
 */
export default function PortalSelect() {
  const [images, setImages] = useState({});
  useEffect(() => { api.get("/portal/images").then(r => setImages(r.data)).catch(() => {}); }, []);
  const nav = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    if (user) nav("/app", { replace: true });
  }, [user, nav]);

  const signInOptions = [
    { title: "Business & Provider Login", detail: "Providers, merchants and company accounts · phone + PIN", icon: KeyRound, href: "/auth/pin" },
    { title: "PIN-only Staff Login", detail: "Staff and team members with an assigned PIN", icon: Smartphone, href: "/admin/icaard" },
  ];
  const providerOptions = signInOptions;

  return (
    <div
      className="relative min-h-screen overflow-hidden text-white bg-gradient-to-br from-[#09090F] via-[#13131E] to-[#09090F]"
      data-testid="portal-select-page"
    >
      {/* Decorative scattered icon field (entire page, behind content) */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {DOODLES.map((d, i) => (
          <FloatIcon key={i} {...d} />
        ))}
        {/* soft radial glow */}
        <div
          className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full"
          style={{ background: "radial-gradient(closest-side, rgba(124,58,237,0.18), transparent)" }}
        />
        <div
          className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full"
          style={{ background: "radial-gradient(closest-side, rgba(244,63,94,0.12), transparent)" }}
        />
      </div>

      {/* Header */}
      <header className="relative border-b border-[#26263A] z-10 pt-safe">
        <div className="max-w-6xl mx-auto px-6 h-28 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2">
            <BrandMark size={96} />
          </Link>
          <Link to="/legal" className="text-xs text-[#9CA3D4] hover:text-white" data-testid="legal-link">
            Legal
          </Link>
        </div>
      </header>

            <div className="icaard-portal-welcome">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#A78BFA]">
              Welcome to iCaard
            </div>

            <h1 className="font-display font-black text-3xl sm:text-5xl mt-2 leading-tight">
              Everyday services, all in one place
            </h1>
            <p className="text-sm sm:text-base text-[#9CA3D4] mt-3 max-w-md">
              Find food, transport, events and local services. Keep your bookings and passes together with iCaard.
            </p>
            </div>

      
      <main className="relative z-10 max-w-6xl mx-auto px-6 py-10 sm:py-14">
        {/* HERO: card image + portal selector */}
        <section className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Card image */}
          <div className="order-2 lg:order-1 relative">
            <div className="absolute inset-0 -m-6 rounded-[2rem] bg-[#7C3AED]/10 blur-2xl" aria-hidden="true" />
            <img
              src={images.hero_image || CARD_IMG}
              alt="iCaard. One Card, One System, Unlimited Access"
              className="relative w-full max-w-md mx-auto drop-shadow-[0_30px_60px_rgba(124,58,237,0.45)] rounded-2xl"
              data-testid="hero-card-image"
              loading="eager"
            />
            <div className="text-center mt-4 text-[11px] tracking-[0.25em] uppercase text-[#A78BFA]">
              One Card · One System · Unlimited Access
            </div>
          </div>

          {/* Portal selector */}
          <div className="order-1 lg:order-2">
            <div className="mb-4" data-testid="portal-intro">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#147D60]">Sign in or Register</div>
              <p className="text-sm text-[#607A6D] mt-2">Choose sign in or registration to enjoy iCaard services and tickets.</p>
            </div>

            <div className="mt-6" data-testid="portal-grid">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="customer-auth-options">
                <Link
                  to="/auth"
                  data-testid="signin-phone-password"
                  className="group flex min-h-28 items-center gap-4 rounded-2xl border border-[#DCE8E0] bg-white p-4 text-left text-[#173B2D] shadow-[0_8px_24px_rgba(20,125,96,0.06)] transition hover:border-[#9BCDB4] hover:bg-[#F8FBF9] sm:p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#147D60] text-white"><Phone size={19} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display text-base font-bold">Sign in</span>
                    <span className="mt-1 block text-[11px] text-[#607A6D]">Already have an account? Use your phone and password.</span>
                  </span>
                  <ArrowRight size={17} className="shrink-0 text-[#147D60]" />
                </Link>
                <Link
                  to="/join"
                  data-testid="register-customer"
                  className="group flex min-h-28 items-center gap-4 rounded-2xl border border-[#B9DDC8] bg-gradient-to-br from-[#F1FAF4] to-[#E5F4EA] p-4 text-left text-[#173B2D] shadow-[0_8px_24px_rgba(20,125,96,0.08)] transition hover:border-[#83C3A0] hover:from-[#EAF7EF] hover:to-[#DDF0E4] sm:p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#D3EDE0] text-[#147D60]"><UserPlus size={19} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display text-base font-bold">Register</span>
                    <span className="mt-1 block text-[11px] text-[#607A6D]">Create an account for iCaard services and tickets.</span>
                  </span>
                  <ArrowRight size={17} className="shrink-0 text-[#147D60]" />
                </Link>
              </div>

              <div className="mt-4" data-testid="login-sponsored-ad">
                <AdsRotator fitCreative />
              </div>

              <details className="service-provider-dropdown" data-testid="provider-signin-options">
                <summary data-testid="provider-signin-toggle">
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#147D60]/10 text-[#147D60]">
                      <Building2 size={17} />
                    </span>
                    <span className="min-w-0">
                      <strong>Business &amp; staff sign-in</strong>
                      <small>For providers, merchants, companies and teams</small>
                    </span>
                  </span>
                  <ChevronDown size={17} />
                </summary>
                <div id="provider-portal-grid" className="grid grid-cols-1 gap-2 p-3" data-testid="provider-portal-grid" style={{ gridTemplateColumns: "1fr", margin: 0 }}>
                  {providerOptions.map(({ title, detail, icon: Icon, href }) => (
                    <button
                      key={href}
                      onClick={() => nav(href)}
                      data-testid={`signin-${href === "/auth/pin" ? "phone-pin" : "pin-only"}`}
                      className="group flex items-center gap-3 rounded-xl border border-[#DDE8E1] bg-white p-3 text-left text-[#173B2D] transition hover:border-[#A9D4B7] hover:bg-[#F8FBF9]"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#147D60]/10 text-[#147D60]">
                        <Icon size={16} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-bold !text-[#173B2D]">{title}</span>
                        <span className="mt-0.5 block text-[10px] !text-[#607A6D]">{detail}</span>
                      </span>
                      <ArrowRight size={15} className="shrink-0 text-[#147D60]" />
                    </button>
                  ))}
                </div>
              </details>

            </div>
          </div>
        </section>

        <section className="mt-12" data-testid="landing-faq">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#A78BFA]">A few quick answers</div>
          <h2 className="font-display font-black text-2xl sm:text-3xl mt-2 leading-tight">Getting started is simple.</h2>
          <div className="grid md:grid-cols-2 gap-3 mt-5">
            <details className="landing-faq-item">
              <summary>What is iCaard?</summary>
              <p>One place to discover services, manage bookings and keep tickets, passes and gift cards ready.</p>
            </details>
            <details className="landing-faq-item">
              <summary>Do I need a physical card?</summary>
              <p>No. You can create a customer account online and use your digital iCaard.</p>
            </details>
            <details className="landing-faq-item">
              <summary>Which portal should I choose?</summary>
              <p>Choose Customer for personal services. If you provide a service or run a business, choose the portal that matches your role.</p>
            </details>
            <details className="landing-faq-item">
              <summary>Need help signing in?</summary>
              <p>Open generic login and use the support link, or email <a href="mailto:info@icaard.com">info@icaard.com</a>.</p>
            </details>
          </div>
        </section>

        <div className="mt-5"><SupportWidget publicWelcome /></div>

        {/* HOW IT WORKS */}
        <section className="mt-20" data-testid="how-it-works">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#A78BFA]">
            How iCaard works
          </div>
          <h2 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">
            Your everyday services, connected.
          </h2>
          <p className="text-sm text-[#9CA3D4] mt-2 max-w-xl">
            One place to discover services, manage bookings and keep your passes ready.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {STEPS.map(({ n, Icon, title, body }) => (
              <div
                key={n}
                data-testid={`step-${n}`}
                className="relative bg-[#13131E]/70 border border-[#26263A] rounded-2xl p-5 backdrop-blur-sm"
              >
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#7C3AED] flex items-center justify-center font-black text-sm shadow-lg">
                  {n}
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#1A1A27] border border-[#26263A] flex items-center justify-center text-[#A78BFA] mb-3">
                  <Icon size={18} />
                </div>
                <div className="font-display font-bold text-base">{title}</div>
                <p className="text-[12px] text-[#9CA3D4] mt-1 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PARTNERS & SHOPS DIRECTORY (Round 53g) */}
        <section className="mt-20" data-testid="partners-directory-section">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#A78BFA]">
            Partners & Shops
          </div>
          <h2 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">
            The iCaard family, in your neighbourhood.
          </h2>
          <p className="text-sm text-[#9CA3D4] mt-2 max-w-xl">
            Discover food vendors, hostels, service providers, print shops and merchants already on iCaard.
            Explore local businesses and service providers listed on iCaard.
          </p>
          <div className="mt-8">
            <PartnersMap limit={200} height={480} />
          </div>
        </section>

        {/* JOIN THE FAMILY */}
        <section
          className="mt-20 relative overflow-hidden rounded-3xl border border-[#26263A] bg-gradient-to-br from-[#13131E] via-[#1A1A27] to-[#09090F] p-8 sm:p-12"
          data-testid="join-family"
        >
          <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-[#7C3AED]/20 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-10 -left-10 w-60 h-60 rounded-full bg-[#F59E0B]/15 blur-3xl" aria-hidden="true" />

          <div className="relative grid lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
            {/* Lifestyle photo — real Ghanaian iCaard holder */}
            <div className="relative" data-testid="lifestyle-hero">
              <div className="absolute -inset-3 rounded-[2rem] bg-[#FCD34D]/10 blur-2xl" aria-hidden="true" />
              <img
                src={images.lifestyle_image || LIFESTYLE_HERO_IMG}
                alt="A proud Ghanaian iCaard holder. Made in Ghana"
                loading="lazy"
                className="relative w-full max-w-md mx-auto rounded-3xl shadow-2xl shadow-[#7C3AED]/30 ring-1 ring-[#26263A]"
              />
              <div className="text-center mt-3 text-[11px] tracking-[0.25em] uppercase text-[#FCD34D]">
                Made in Ghana · For Ghana
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#FCD34D]">
                Join the iCaard family
              </div>
              <h2 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">
                One account for your everyday services.
              </h2>
              <p className="text-sm text-[#9CA3D4] mt-3 max-w-xl">
                Whether you&apos;re a student, a market trader, a hostel owner, an event organiser, a transport
                operator or a print shop owner, there&apos;s a portal built for you.
              </p>

              <div className="flex flex-wrap gap-3 mt-6">
                <a
                  href="mailto:info@icaard.com"
                  data-testid="join-cta-email"
                  className="inline-flex items-center gap-2 border border-[#26263A] hover:border-[#7C3AED] text-white font-bold text-sm px-5 py-3 rounded-xl transition"
                >
                  <Mail size={14} /> Become a partner
                </a>
              </div>
            </div>
          </div>

          {/* WhatsApp channel + contact — secondary row inside the same panel. */}
          <div className="relative mt-10 pt-10 border-t border-[#26263A] grid lg:grid-cols-2 gap-6">
            <WhatsAppChannelCard variant="wide" />

            {/* Contact card */}
            <div className="bg-[#09090F]/70 border border-[#26263A] rounded-2xl p-6 backdrop-blur-sm">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#A78BFA]">
                Talk to us
              </div>
              <div className="mt-3 space-y-3 text-sm">
                <a
                  href="mailto:info@icaard.com"
                  className="flex items-center gap-3 hover:text-[#A78BFA] transition"
                  data-testid="contact-email"
                >
                  <span className="w-9 h-9 rounded-lg bg-[#7C3AED]/20 border border-[#7C3AED]/40 flex items-center justify-center text-[#A78BFA]">
                    <Mail size={16} />
                  </span>
                  <span className="font-medium">info@icaard.com</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-[#26263A] mt-16">
        <div className="max-w-6xl mx-auto px-6 pt-8 pb-2" data-testid="seo-about">
          <h2 className="font-display font-bold text-lg">About iCaard</h2>
          <p className="text-xs text-[#9CA3D4] mt-2 leading-relaxed max-w-3xl">
            iCaard brings everyday services together: food orders, events, transport tickets, gift cards,
            hostels, delivery, printing, professional services and smart queues. Customers can manage
            their passes, bookings, activity and profiles in one place. Dedicated portals help providers
            manage their services, while support agents help customers with approved, read only access.
            Made in Ghana, for Ghana.
          </p>
        </div>
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between text-[10px] text-[#9CA3D4]">
          <div>iCaard™ © 2026 · One Card, One System, Unlimited Access</div>
          <div className="flex flex-wrap gap-4">
            <a href="mailto:info@icaard.com" className="hover:text-white">info@icaard.com</a>
            <Link to="/legal" className="hover:text-white">Legal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
